import React, { useState } from 'react';
import { EYEBROW, MONO, NOTE } from './typeScale';

/**
 * The keys, shown once.
 *
 * <h3>Why this is a step and not a notification</h3>
 *
 * The onboard mints the gateway credential and, depending on the path, an SDK
 * or MCP key. The plaintext appears on the wire exactly once — only bcrypt
 * hashes are persisted, and nothing returns them again. A surface that
 * registers an agent and never shows them has handed somebody an agent they
 * cannot wire up, and the only remedy is to register a second one.
 *
 * So the step carries a forcing function: moving on is a thing they say, not a
 * thing that happens while they are reading.
 */
export default function CredentialsAsk({ envelope, onAnswer, busy = false }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(null);

  const keys = [
    ['Gateway key', envelope?.gatewayKey],
    ['SDK key', envelope?.sdkKey],
    ['MCP key', envelope?.mcpKey],
  ].filter(([, k]) => k && k.plaintext);

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
    } catch {
      /* A browser that refuses the clipboard is not a failure of the step —
         the key is on screen and can be selected. */
      setCopied(null);
    }
  };

  if (!keys.length) {
    return (
      <p style={NOTE}>
        No credentials came back with this registration. The agent is registered; its keys can
        be reissued from its page in the full platform.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
      <p style={NOTE}>
        These are shown once. Nothing stores the full key — copy them somewhere safe before you
        move on.
      </p>

      {keys.map(([label, k]) => (
        <div key={label} style={{ border: '1px solid var(--p7-line)',
                                  borderRadius: 'var(--p7-radius-control)',
                                  padding: '11px 13px', background: 'var(--p7-surface-sunken)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ ...EYEBROW }}>
              {k.name || label}
            </span>
            <button type="button" className="p7-btn p7-btn--sm"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => copy(label, k.plaintext)}>
              {copied === label ? 'Copied' : 'Copy'}
            </button>
          </div>
          <code style={{ display: 'block', marginTop: '6px', wordBreak: 'break-all',
                         ...MONO, color: 'var(--p7-ink)' }}>
            {k.plaintext}
          </code>
        </div>
      ))}

      <label style={{ display: 'flex', alignItems: 'center', gap: '9px',
                      font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)' }}>
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        I have saved these keys
      </label>

      <div>
        <button type="button" className="p7-btn p7-btn--primary" disabled={busy || !saved}
                onClick={() => onAnswer({ saved: true, keys: keys.map(([l]) => l) })}>
          Continue
        </button>
      </div>
    </div>
  );
}

